import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Cookies from 'universal-cookie/es6'
import { useDispatch, useSelector } from 'react-redux'
import { DateTime } from 'luxon'

import { AppState } from '../../../store'
import { WorkflowTypes } from '../../../common/workflow'
import { STORAGEKEYS } from '../../../common/keys'
import { ICurrentLeadPartnerData, IUseFailPageLogicReturn } from '../models/IFailPageModel'
import { ILeadStatisticRecordParams, loanInitService, LeadStatisticEventType } from '../../../service/LoanInitService'
import { SessionStorage } from '../../../common/cookie-storage'
import { IAffiliateDataModel } from '../../../models/form/IFormSubmitionModel'
import { Environment } from '../../../environment/app-settings'
import { sleep } from '../../../common/helpers/helpers'
import { SET_USER_TRAPPED_ON_PAGE_URL_PATH } from '../../../store/actions/application-actions'
import { AppLinks } from '../../../environment/app-links'


/**
 * Custom hook obsługujący logikę strony decyzji negatywnej w aplikacji
*/
const useFailPageLogic = (): IUseFailPageLogicReturn  => {
   const dispatch = useDispatch()
   const sessionStorage = new SessionStorage()

   const applicationState = useSelector((state: AppState) => state.applicationState)
   const globalConfig = useSelector((state: AppState) => state.lenConfigState.globalConfig) ?? ""

   const hasRedirectedRef = useRef(false)

   const [leadSaleLink, setLeadSaleLink] = useState<string | null>(null)
   const [currentLeadPartnerData, setCurrentLeadPartnerData] = useState<ICurrentLeadPartnerData | undefined>(undefined)
   const [leadRedirectionSecondsLeft, setLeadRedirectionSecondsLeft] = useState<number>(0)
   const [isInitializing, setIsInitializing] = useState(true)
   const [leadRedirectValidTo, setLeadRedirectValidTo] = useState<DateTime | null>(() => {
      const validTo = sessionStorage.get(STORAGEKEYS.LEAD_REDIRECT_VALID_TO)
      return validTo ? DateTime.fromISO(validTo) : null
   })

   
   /**
    * Funkcja tworząca linki dla sprzedaży lead'ów na podstawie nazwy aktualnego partnera afiliacyjnego oraz zapisująca dane aktualnego lead'a do stanu.
    * 
    * @param {string} partnerName - nazwa partnera pobrana z cookies'a afiliacyjnego (partnerId) w przypadku gdy taki został utworzony, lub "OpenaNull"
    * 
    * @returns {string | null} - Utworzony link afiliacyjny lub null
   */
   const createLeadSaleLink = (partnerName: string): string | null => {
      const leadBaseLinks = globalConfig.leadBaseLinks
      let leadSaleLink = null

      for (const [key, leadBaseObject] of Object.entries(leadBaseLinks)) {
         if(key.toLowerCase() === partnerName.toLowerCase()){
            leadSaleLink = leadBaseObject.link.replace("{CLICK_ID}", applicationState.appId).replace("{PARTNER_ID}", leadBaseObject.id)

            setCurrentLeadPartnerData({ // Zapisujemy dane o aktualnym partnerze, tak aby można z nich było skorzystać przy zapisie danych do bazy SQL
               partnerName,
               partnerId: leadBaseObject.id,
               clickId: applicationState.appId
            })
         }
      }

      return leadSaleLink
   }

   const addLeadStatisticRecord = async(eventType: LeadStatisticEventType) => {
      const alphaId = sessionStorage.get(STORAGEKEYS.SET_ALPHA_ID)

      if(currentLeadPartnerData !== undefined && leadSaleLink !== null){

         const leadStatisticParams: ILeadStatisticRecordParams = {
            alphaId,
            leadPartner: currentLeadPartnerData.partnerName,
            leadId: currentLeadPartnerData.partnerId,
            redirectionLink: leadSaleLink,
            clickId: currentLeadPartnerData.clickId,
            eventType
         }

         await loanInitService.addLeadStatisticRecord(leadStatisticParams)
      }
   }

   useEffect(() => {
      // MECHANIZM ZATRZYMYWANIA UŻYTKOWNIKA NA STRONIE 
      dispatch({ type: SET_USER_TRAPPED_ON_PAGE_URL_PATH, payload: AppLinks.fail });

      (async() => {
         try {
            if(applicationState.workflowId === WorkflowTypes.LEN_PAYTREE_NORMAL){ // Sprawdzamy czy klient pochodzi z naszego formularza - tylko takich klientów bierzemy pod uwage przy mechanizmie lead'ów
               const cookies = new Cookies()
               const affiliateData: IAffiliateDataModel = cookies.get(STORAGEKEYS.FORM_AFFILIATE_DATA_COOKIE)
               
               // Sprawdzamy czy jest utworzone jakiekolwiek ciasteczko afiliacyjne, jeśli nie oznacza to partnera "OpenaNull"
               const currentPartnerKey = affiliateData?.partnerId ?? Environment.LEAD_OPENA_NULL_KEY
               setLeadSaleLink(createLeadSaleLink(currentPartnerKey))
      
               await sleep(1000) // Opóźniamy żeby loader nie "migał"
      
               if (!leadRedirectValidTo) {
                  const validTo = DateTime.utc().plus({ seconds: globalConfig.leadRedirectionDelayInSeconds })
                  
                  sessionStorage.add(STORAGEKEYS.LEAD_REDIRECT_VALID_TO, validTo.toISO())
                  setLeadRedirectValidTo(validTo)
               }
            }
         }
         catch (e: any) {
            
         }
         finally {
            setIsInitializing(false)
         }
      })()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])


   useEffect(() => {
      if (!leadRedirectValidTo || !leadSaleLink) {
         return
      }

      const update  = () => {
         const now = DateTime.utc()
         const diffMs = leadRedirectValidTo.diff(now).milliseconds
         const diffSeconds = Math.ceil(diffMs / 1000)
   
         if (diffSeconds <= 0) {
   
            // Zabezpieczenie przed wielokrotnym wejściem
            if (hasRedirectedRef.current) {
               return
            }

            hasRedirectedRef.current = true
            setLeadRedirectionSecondsLeft(0)

            if(sessionStorage.get(STORAGEKEYS.LEAD_REDIRECT_DONE) === 'true'){ // Dodatkowe zabezpieczenie przed wielokrotnym wejściem, na wypadek gdyby z jakiegoś powodu ref nie zadziałał
               window.location.replace(leadSaleLink)

               return
            }

            sessionStorage.add(STORAGEKEYS.LEAD_REDIRECT_DONE, 'true')
            addLeadStatisticRecord(LeadStatisticEventType.AUTO).finally(() => { window.location.replace(leadSaleLink) })
            
            return
         }
   
         setLeadRedirectionSecondsLeft(diffSeconds)
   
      }

      update()
      const interval = setInterval(update, 1000)
      return () => clearInterval(interval)

   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [ leadRedirectValidTo, leadSaleLink ])


   return {
      leadSaleLink,
      isInitializing,
      leadRedirectionSecondsLeft,
      addLeadStatisticRecord
   }
}

export default useFailPageLogic